import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem'
import { withAnswersLock } from '@/services/offline-answers-lock'

const DATA_FOLDER = 'offline_data'
const ANSWERS_FILE = 'answers.json'
const ACTION_PLANS_FILE = 'action_plans.json'
// Intenciones de CIERRE de auditoria que no se pudieron entregar todavia (ni por SQLite ni por red).
// Vive aparte de las respuestas a proposito: es la unica pista de "el usuario ya finalizo" cuando
// SQLite no abrio, y NO debe borrarse junto con ellas (ver clearAll/clearForOccurrence abajo).
const PENDING_CLOSES_FILE = 'pending_closes.json'
// Firmas que no se pudieron guardar en SQLite ni enviar (src/offline/pending-signatures.js). Aparte de las
// respuestas por lo mismo que los cierres: no se borran al limpiar respuestas ya sincronizadas.
const PENDING_SIGNATURES_FILE = 'pending_signatures.json'
// Comentarios adicionales de la ocurrencia que no se pudieron guardar en SQLite ni enviar
// (src/offline/pending-comments.js, F-97). Aparte por lo mismo que las firmas.
const PENDING_COMMENTS_FILE = 'pending_comments.json'

const fileExists = async path => {
  try {
    await Filesystem.stat({ path, directory: Directory.Data })
    return true
  } catch {
    return false
  }
}

const keepDamagedFile = (path, data) => Filesystem.writeFile({
  path: `${path}.danado-${Date.now()}`,
  data,
  directory: Directory.Data,
  encoding: Encoding.UTF8,
  recursive: true
})

const readJSONFile = async fileName => {
  if (!Capacitor.isNativePlatform()) {
    const raw = localStorage.getItem(fileName)
    return raw ? JSON.parse(raw) : []
  }

  const path = `${DATA_FOLDER}/${fileName}`
  let data
  try {
    ({ data } = await Filesystem.readFile({ path, directory: Directory.Data, encoding: Encoding.UTF8 }))
  } catch (error) {
    if (!(await fileExists(path))) return []
    throw error
  }

  try {
    return JSON.parse(data)
  } catch (error) {
    await keepDamagedFile(path, data)
    console.warn(`[offline-storage] ${fileName} no se pudo leer, se guardó una copia y se continúa vacío:`, error)
    return []
  }
}

const writeJSONFile = async (fileName, data) => {
  if (!Capacitor.isNativePlatform()) {
    localStorage.setItem(fileName, JSON.stringify(data))
    return
  }

  await Filesystem.writeFile({
    path: `${DATA_FOLDER}/${fileName}`,
    data: JSON.stringify(data),
    directory: Directory.Data,
    encoding: Encoding.UTF8,
    recursive: true
  })
}

export const OfflineStorage = {

  async getAnswers () {
    return readJSONFile(ANSWERS_FILE)
  },

  async setAnswers (answers) {
    return writeJSONFile(ANSWERS_FILE, answers)
  },

  async getActionPlans () {
    return readJSONFile(ACTION_PLANS_FILE)
  },

  async setActionPlans (plans) {
    return writeJSONFile(ACTION_PLANS_FILE, plans)
  },

  async getPendingCloses () {
    return readJSONFile(PENDING_CLOSES_FILE)
  },

  async setPendingCloses (pendingCloses) {
    return writeJSONFile(PENDING_CLOSES_FILE, pendingCloses)
  },

  async getPendingSignatures () {
    return readJSONFile(PENDING_SIGNATURES_FILE)
  },

  async setPendingSignatures (pendingSignatures) {
    return writeJSONFile(PENDING_SIGNATURES_FILE, pendingSignatures)
  },

  async getPendingComments () {
    return readJSONFile(PENDING_COMMENTS_FILE)
  },

  async setPendingComments (pendingComments) {
    return writeJSONFile(PENDING_COMMENTS_FILE, pendingComments)
  },

  // OJO: clearAll y clearForOccurrence NO tocan pending_closes.json, pending_signatures.json ni
  // pending_comments.json a proposito. Un cierre pendiente
  // representa trabajo del usuario que el servidor todavia no conoce; borrarlo al limpiar respuestas
  // (que se limpian justo cuando YA se sincronizaron) dejaria la auditoria abierta para siempre. Las
  // intenciones de cierre las da de baja src/offline/pending-close.js cuando se aplican de verdad.
  async clearAll () {
    await withAnswersLock(async () => {
      await writeJSONFile(ANSWERS_FILE, [])
      await writeJSONFile(ACTION_PLANS_FILE, [])
    })
  },

  async clearForOccurrence (occurrenceId) {
    await withAnswersLock(async () => {
      const all = await readJSONFile(ANSWERS_FILE)
      const remaining = all.filter(a => String(a.occurrence_id).trim() !== String(occurrenceId).trim())
      await writeJSONFile(ANSWERS_FILE, remaining)
    })
  }
}
